import Flex from "@/components/atoms/Flex";
import SoftBadge from "@/components/atoms/SoftBadge";
import CompanyEmailLog, {
  CompanyEmailStatus,
  CompanyEmailType,
} from "@/interfaces/CompanyEmailLog";
import { IFilters, IMeta } from "@/interfaces/Fetch";
import {
  faCheckCircle,
  faClock,
  faEnvelope,
  faExclamationCircle,
  faExternalLinkAlt,
  faFileAlt,
  faUser,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { DateTime } from "luxon";
import { Dispatch, FC, SetStateAction } from "react";
import { Col, Row } from "react-bootstrap";
import { Link } from "react-router-dom";
import PaginateCard from "../PaginateCard";

export interface EmailLogsCardProps {
  data?: CompanyEmailLog[];
  meta?: IMeta;
  filters: IFilters;
  setFilters: Dispatch<SetStateAction<IFilters>>;
}

const getStatusBadge = (status: CompanyEmailStatus) => {
  switch (status) {
    case "sent":
      return (
        <SoftBadge bg="success">
          <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
          Envoyé
        </SoftBadge>
      );
    case "pending":
      return (
        <SoftBadge bg="warning">
          <FontAwesomeIcon icon={faClock} className="me-1" />
          En attente
        </SoftBadge>
      );
    case "failed":
      return (
        <SoftBadge bg="danger">
          <FontAwesomeIcon icon={faExclamationCircle} className="me-1" />
          Échec
        </SoftBadge>
      );
  }
};

const getTypeLabel = (type: CompanyEmailType) => {
  switch (type) {
    case "training_brochures":
      return "Plaquettes de formation";
    case "conventions":
      return "Conventions";
  }
};

const EmailLogsCard: FC<EmailLogsCardProps> = ({
  data = [],
  meta,
  filters,
  setFilters,
}) => {
  return (
    <PaginateCard filters={filters} setFilters={setFilters} meta={meta}>
      {data.length === 0 ? (
        <div className="text-center py-5">
          <FontAwesomeIcon
            icon={faEnvelope}
            className="text-muted mb-3"
            size="3x"
          />
          <p className="text-muted">Aucun email envoyé pour cette entreprise</p>
        </div>
      ) : (
        data.map(
          ({
            id,
            type,
            status,
            sentAt,
            createdAt,
            sentBy,
            recipients,
            trainingBrochures,
            errorMessage,
          }) => {
            const mainRecipient = recipients?.find((r) => r.type === "to");
            const sentDate = sentAt
              ? DateTime.fromISO(sentAt)
              : DateTime.fromISO(createdAt);

            return (
              <div
                key={id}
                className="bg-white dark__bg-1100 p-x1 rounded-3 shadow-sm mb-3"
              >
                <Row className="align-items-center g-3">
                  {/* Type et Statut */}
                  <Col lg={12} xxl={3}>
                    <Flex>
                      <div className="flex-1">
                        <h6 className="mb-1">{getTypeLabel(type)}</h6>
                        <div className="mb-2">{getStatusBadge(status)}</div>
                        <small className="text-muted">
                          <FontAwesomeIcon icon={faClock} className="me-1" />
                          {sentDate.toFormat("dd/MM/yyyy à HH:mm")}
                        </small>
                      </div>
                    </Flex>
                  </Col>

                  {/* Destinataire */}
                  <Col lg={12} xxl={3}>
                    <div className="text-muted mb-1">
                      <FontAwesomeIcon icon={faEnvelope} className="me-1" />
                      <small>Destinataire</small>
                    </div>
                    <div className="fw-medium">
                      {mainRecipient?.name || "N/A"}
                    </div>
                    <small className="text-muted">{mainRecipient?.email}</small>
                  </Col>

                  {/* Pièces jointes */}
                  <Col lg={12} xxl={3}>
                    <div className="text-muted mb-1">
                      <FontAwesomeIcon icon={faFileAlt} className="me-1" />
                      <small>Pièces jointes</small>
                    </div>
                    <div className="fw-medium">
                      {trainingBrochures?.length || 0} plaquette(s)
                    </div>
                    {trainingBrochures && trainingBrochures.length > 0 && (
                      <div className="mt-1">
                        {trainingBrochures.map((brochure) => (
                          <div key={brochure.id}>
                            <a
                              href={brochure.file?.link || "#"}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary text-decoration-none d-inline-flex align-items-center"
                            >
                              <small>
                                {brochure.label}
                                <FontAwesomeIcon
                                  icon={faExternalLinkAlt}
                                  className="ms-1"
                                  size="xs"
                                />
                              </small>
                            </a>
                          </div>
                        ))}
                      </div>
                    )}
                  </Col>

                  {/* Envoyé par */}
                  <Col lg={12} xxl={3}>
                    <div className="text-muted mb-1">
                      <FontAwesomeIcon icon={faUser} className="me-1" />
                      <small>Envoyé par</small>
                    </div>
                    {sentBy ? (
                      <Link
                        to={`/users/${sentBy.id}`}
                        className="text-decoration-none"
                      >
                        <div className="fw-medium text-primary">
                          {sentBy.fullname}
                        </div>
                      </Link>
                    ) : (
                      <div className="fw-medium text-muted">N/A</div>
                    )}
                  </Col>
                </Row>

                {/* Message d'erreur si échec */}
                {status === "failed" && errorMessage && (
                  <Row className="mt-3">
                    <Col>
                      <div className="alert alert-danger mb-0 py-2">
                        <small>
                          <strong>Erreur :</strong> {errorMessage}
                        </small>
                      </div>
                    </Col>
                  </Row>
                )}
              </div>
            );
          }
        )
      )}
    </PaginateCard>
  );
};

export default EmailLogsCard;
